import {
  calculateCampaignPrice,
  isProductExcludedByActiveCampaign,
} from '@/lib/utils/campaign-price'
import { useIsBuy1Get1ValueMatchCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { CAMPAIGN_NAMES } from '@/lib/utils/campaign/campaign-names'
import {
  useAllCampaigns,
  useCampaign,
  useCampaignActive,
} from '@/providers/campaign-provider'
import { ProductFormatted } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { useEffect, useMemo, useState } from 'react'
import { useCartMiniQuery } from '../react-query/cart/use-cart'

interface UseCampaignPriceProps {
  regularPrice: number
  comparePrice: number
  productId?: string | number
  product?: ProductFormatted | ProductDetailFormatted
}

interface UseCampaignPriceResult {
  finalPrice: number
  originalPrice: number
  discountPercent: number
  shouldShowDiscount: boolean
  isLoading: boolean
}

/**
 * Hook tính toán giá cuối cùng của sản phẩm dựa trên regularPrice, comparePrice, campaign (buy_more_save_more, buy_1_get_1_value_match) và promotion trong giỏ hàng.
 * - Nếu buy_1_get_1_value_match active và regularPrice <= max_eligible_price_for_gift thì giảm giá về 0.
 * - Nếu max_promotion_amount = 0 thì không được giảm, giữ nguyên giá gốc.
 * - Nếu max_promotion_amount > 0 thì chỉ giảm cho item có giá gốc < max_promotion_amount.
 * - Phần trăm giảm giá hiển thị không vượt quá campaignPercent (chỉ ảnh hưởng UI, không tính lại giá).
 * - Trả về giá cuối cùng, phần trăm giảm giá, trạng thái loading, v.v.
 * - Sử dụng calculateCampaignPrice utility function để đảm bảo logic đồng bộ.
 */
export function useCampaignPrice({
  regularPrice,
  comparePrice,
  product,
}: UseCampaignPriceProps): UseCampaignPriceResult {
  // Lấy thông tin giỏ hàng (bao gồm max_promotion_amount và max_eligible_price_for_gift)
  const { data: cartMini, isLoading: isCartLoading } = useCartMiniQuery({
    enabled: true,
  })
  // Lấy tất cả campaigns để detect campaign đang được áp dụng
  const { campaigns } = useAllCampaigns()
  // Kiểm tra campaign "buy_more_save_more" có đang hoạt động không
  const campaignData = useCampaign(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  const isCampaignActive = useCampaignActive(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  // Kiểm tra campaign "buy_1_get_1_value_match" có đang hoạt động không
  const isBuy1Get1ValueMatchActive = useIsBuy1Get1ValueMatchCampaignActive()

  const isExcluded = useMemo(
    () =>
      isProductExcludedByActiveCampaign({
        product,
        campaigns,
        regularPrice,
        maxEligiblePriceForGift: cartMini?.max_eligible_price_for_gift,
      }),
    [product, campaigns, regularPrice, cartMini?.max_eligible_price_for_gift]
  )

  // Tính toán giá cuối cùng dựa trên campaign và max_promotion_amount
  const { finalPrice, originalPrice, discountPercent, shouldShowDiscount } =
    useMemo(() => {
      // Sử dụng utility function để tính campaign price
      return calculateCampaignPrice({
        regularPrice,
        comparePrice,
        maxPromotionAmount: cartMini?.max_promotion_amount ?? 0,
        campaignPercent: (campaignData?.data?.percent ?? 0) as number,
        isCampaignActive: isCampaignActive,
        isExcluded,
        maxEligiblePriceForGift: cartMini?.max_eligible_price_for_gift,
        isBuy1Get1ValueMatchActive: isBuy1Get1ValueMatchActive,
      })
    }, [
      regularPrice,
      comparePrice,
      cartMini?.max_promotion_amount,
      cartMini?.max_eligible_price_for_gift,
      campaignData?.data?.percent,
      isCampaignActive,
      isExcluded,
      isBuy1Get1ValueMatchActive,
    ])

  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard client-mounted guard to avoid SSR/hydration mismatch
    setIsMounted(true)
  }, [])

  return {
    finalPrice,
    originalPrice,
    discountPercent,
    shouldShowDiscount,
    isLoading: isCartLoading || !isMounted,
  }
}
